// pages/approvals/MyPendingApprovalsPage.tsx
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, DollarSign, Building, ArrowRight, User, Files } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingApprovals } from '@/hooks/finance/usePaymentRequests';
import {
  useBulkPaymentRequestsForApprover,
  type BulkPaymentRequestGroup,
} from '@/hooks/finance/usePaymentRequestsBulk';
import { format } from 'date-fns';

const MyPendingApprovalsPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: pendingRequests = [], isLoading: isSingleLoading } = usePendingApprovals();
  const { data: bulkPendingRequests = [], isLoading: isBulkLoading } =
    useBulkPaymentRequestsForApprover('pending_approval');

  const isLoading = isSingleLoading || isBulkLoading;

  const formatCurrency = (amount: number, currency = 'ETB') => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const singlePendingRequests = useMemo(() => {
    const bulkUploadIds = new Set(bulkPendingRequests.map((group) => group.uploadId));

    return pendingRequests.filter((req) => {
      if (req.upload_id && bulkUploadIds.has(req.upload_id)) {
        return false;
      }

      if (req.upload_method === 'bulk_csv' && req.upload_id) {
        return false;
      }

      return true;
    });
  }, [pendingRequests, bulkPendingRequests]);

  type SingleApprovalRow = (typeof singlePendingRequests)[number];

  type DisplayItem =
    | { type: 'single'; request: SingleApprovalRow; sortDate: string }
    | { type: 'bulk'; group: BulkPaymentRequestGroup; sortDate: string };

  const displayItems = useMemo<DisplayItem[]>(() => {
    const singleItems: DisplayItem[] = singlePendingRequests.map((request) => ({
      type: 'single',
      request,
      sortDate: request.submitted_at || request.created_at,
    }));

    const bulkItems: DisplayItem[] = bulkPendingRequests.map((group) => ({
      type: 'bulk',
      group,
      sortDate: group.createdAt,
    }));

    return [...bulkItems, ...singleItems].sort(
      (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
    );
  }, [singlePendingRequests, bulkPendingRequests]);

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Pending Approvals</h1>
          <p className="text-muted-foreground mt-1">
            Payment requests awaiting your approval
          </p>
        </div>

        {displayItems.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No Pending Approvals</h3>
              <p className="text-muted-foreground">
                You don&apos;t have any payment requests waiting for your approval.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {displayItems.map((item) => {
              if (item.type === 'bulk') {
                const { group } = item;

                return (
                  <Card
                    key={`bulk-${group.uploadId}`}
                    className="hover:shadow-md transition-shadow cursor-pointer border-dashed"
                    onClick={() => navigate(`/my-approvals/bulk/${group.uploadId}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="secondary" className="gap-1">
                              <Files className="h-3 w-3" />
                              Bulk request
                            </Badge>

                            {group.bulkStatus !== 'mixed' ? (
                              <Badge variant="outline">
                                {group.bulkStatus.replace('_', ' ')}
                              </Badge>
                            ) : (
                              <Badge variant="outline">Mixed statuses</Badge>
                            )}
                          </div>

                          <h3 className="font-semibold text-lg">Uploaded from CSV</h3>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Submitted {format(new Date(group.createdAt), 'MMM d, yyyy')}
                            </span>

                            <span>{group.requestsCount} requests</span>

                            {group.department && (
                              <span className="flex items-center gap-1">
                                <Building className="h-4 w-4" />
                                {group.department.name}
                              </span>
                            )}

                            {group.categories.length > 0 && (
                              <span>{group.categories.map((cat) => cat.name).join(', ')}</span>
                            )}
                          </div>
                        </div>

                        <div className="text-right flex flex-col items-end gap-2">
                          <div className="flex items-center gap-1 text-xl font-bold">
                            <DollarSign className="h-5 w-5" />
                            {formatCurrency(group.totalAmount, group.currency || 'ETB')}
                          </div>
                          <Button variant="ghost" size="sm" className="gap-1">
                            Review <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              const request = item.request;

              return (
                <Card
                  key={request.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/my-approvals/${request.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono">
                            {request.request_number}
                          </Badge>
                          <Badge variant="secondary">
                            Level {request.current_approval_level}
                          </Badge>
                        </div>

                        <h3 className="font-semibold text-lg">{request.title}</h3>

                        {request.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {request.description}
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {(request.requester_first_name || request.requester_last_name) && (
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {`${request.requester_first_name || ''} ${request.requester_last_name || ''}`.trim()}
                            </span>
                          )}

                          {request.department && (
                            <span className="flex items-center gap-1">
                              <Building className="h-4 w-4" />
                              {request.department.name}
                            </span>
                          )}

                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            Submitted{' '}
                            {format(
                              new Date(request.submitted_at || request.created_at),
                              'MMM d, yyyy'
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex flex-col items-end gap-2">
                        <div className="flex items-center gap-1 text-xl font-bold">
                          <DollarSign className="h-5 w-5" />
                          {formatCurrency(request.total_amount, request.currency || 'ETB')}
                        </div>
                        <Button variant="ghost" size="sm" className="gap-1">
                          Review <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyPendingApprovalsPage;